import { SplitModal, type SplitMember, type SplitResultEntry } from './SplitModal';

export interface PaidBySplitModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The FULL group — the modal scopes what's listed itself (see `payerIds`). */
  members: SplitMember[];
  /** The member ids picked as payers in the Paid-by modal — the initial split participants. */
  payerIds: readonly string[];
  /** The splittable (entry-space) total: the bill less the wallet's contribution. */
  totalMinor: number;
  currency: string;
  payerName: string;
  onSave: (entries: SplitResultEntry[]) => void;
}

/**
 * The Split popup CHAINED from a multi-payer Paid-by save — a separate component from the Split
 * field's own `SplitModal` so the two flows can never leak state into each other, per the flow
 * spec: it opens in the "selected members" mode — ONLY the Paid-by payers are listed, splitting
 * the remaining amount among them (e.g. 100 left of a 150 bill → 50/50), and they STAY the
 * participants on every split-by tab (evenly / shares / percentage / amount alike). Whole-group
 * splits belong exclusively to the Split field's own `SplitModal`.
 *
 * It composes the same `SplitModal` engine (tabs, checkboxes, live amounts, validation) rather
 * than duplicating that logic — the payer scoping comes exclusively from the `payerIds` seed +
 * `splitMode="selectedMembers"` built here, so the Paid-by selection never reaches the Split
 * field's modal, which always opens whole-group.
 */
export function PaidBySplitModal({
  open,
  onOpenChange,
  members,
  payerIds,
  totalMinor,
  currency,
  payerName,
  onSave,
}: PaidBySplitModalProps) {
  const payerSet = new Set(payerIds);
  // The seed marks EVERY member with an explicit enabled flag (payers on, the rest off) — amounts
  // are recomputed by the form itself once open, so 0/'Equal' placeholders suffice here.
  const seed: SplitResultEntry[] = members.map((mem) => ({
    userId: mem.id,
    expenseAmountMinor: 0,
    splitEnabled: payerSet.has(mem.id),
    splitType: 'Equal',
  }));
  return (
    <SplitModal
      open={open}
      onOpenChange={onOpenChange}
      members={members}
      totalMinor={totalMinor}
      currency={currency}
      payerName={payerName}
      initial={seed}
      splitMode="selectedMembers"
      onSave={onSave}
    />
  );
}
